import type { PropsWithChildren, ReactNode } from 'react'
import { useRef, useState, createContext, useContext } from 'react'

type ModalSheetProviderProps = PropsWithChildren

type ModalSheetVariation = 'default' | 'desktopInset'

interface ModalSheetContextValue {
  isModalOpen: boolean
  setModalConfig: React.Dispatch<React.SetStateAction<ModalSheetConfig>>
  modalConfig: ModalSheetConfig
  openModal: (config: ModalSheetConfig, variation?: ModalSheetVariation) => void
  closeModal: () => void
}

export interface ModalSheetConfig {
  content: ReactNode
  contentHasInternalScroll?: boolean
  height?: string
  title?: string
  width?: string
  position?: 'bottom' | 'left' | 'right'
  variation?: ModalSheetVariation
  onClose?: () => void
  header?: {
    isVisible: boolean
    component: ReactNode
  }
}

const ModalSheetContext = createContext<ModalSheetContextValue>(
  {} as ModalSheetContextValue
)

export const ModalSheetProvider = ({ children }: ModalSheetProviderProps) => {
  const defaultConfig: ModalSheetConfig = {
    content: <></>,
    variation: 'default',
    header: { isVisible: true, component: undefined },
  }

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalConfig, setModalConfig] =
    useState<ModalSheetConfig>(defaultConfig)

  const isScrollLockedRef = useRef(false)
  const previousScrollStyleRef = useRef<{
    bodyOverflow?: string
    htmlOverflow?: string
    htmlOverscrollBehavior?: string
    hadNoScrollClass: boolean
  } | null>(null)

  const openModal = (
    config: ModalSheetConfig,
    variation: ModalSheetVariation = config.variation ?? 'default'
  ) => {
    setModalConfig({ ...config, variation })
    setIsModalOpen(true)

    if (typeof document === 'undefined' || !document?.body?.style) {
      return
    }

    if (isScrollLockedRef.current) {
      return
    }

    previousScrollStyleRef.current = {
      bodyOverflow: document.body.style.overflow,
      htmlOverflow: document.documentElement.style.overflow,
      htmlOverscrollBehavior: document.documentElement.style.overscrollBehavior,
      hadNoScrollClass:
        document.documentElement.classList.contains('no-scroll'),
    }

    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    document.documentElement.style.overscrollBehavior = 'none'
    document.documentElement.classList.add('no-scroll')
    isScrollLockedRef.current = true
  }

  const closeModal = () => {
    modalConfig?.onClose?.()

    setIsModalOpen(false)
    setModalConfig(defaultConfig)

    if (typeof document === 'undefined' || !document?.body?.style) {
      return
    }

    if (!isScrollLockedRef.current) {
      return
    }

    const previousStyles = previousScrollStyleRef.current

    document.body.style.overflow = previousStyles?.bodyOverflow ?? ''
    document.documentElement.style.overflow = previousStyles?.htmlOverflow ?? ''
    document.documentElement.style.overscrollBehavior =
      previousStyles?.htmlOverscrollBehavior ?? ''

    if (!previousStyles?.hadNoScrollClass) {
      document.documentElement.classList.remove('no-scroll')
    }

    previousScrollStyleRef.current = null
    isScrollLockedRef.current = false
  }

  const value = {
    isModalOpen,
    setModalConfig,
    modalConfig,
    openModal,
    closeModal,
  }

  return (
    <ModalSheetContext.Provider value={value}>
      {children}
    </ModalSheetContext.Provider>
  )
}

export const useModalSheet = () => {
  const context = useContext(ModalSheetContext)

  if (context === undefined) {
    throw new Error('Do not use Modal outside the Modal context.')
  }

  return context
}
